import React, { useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip, IconButton, Menu, MenuItem,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, TextField, CircularProgress, Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { subscribeProductRequests, updateProductRequest, deleteProductRequest } from '../../services/db';
import { notifyInternal, notifyCustomer } from '../../services/email';
import { quoteEmail } from '../../services/emailTemplates';

const STATUS_COLORS = {
  Pending:  { bg: 'rgba(230,126,34,0.15)',  color: '#e67e22' },
  Quoted:   { bg: 'rgba(41,128,185,0.15)',   color: '#2980b9' },
  Accepted: { bg: 'rgba(39,174,96,0.15)',    color: '#27ae60' },
  Declined: { bg: 'rgba(192,57,43,0.15)',    color: '#c0392b' },
};

function ProductRequests({ searchQuery }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [quotedPrice, setQuotedPrice] = useState('');
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteAlert, setQuoteAlert] = useState('');
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    let unsubscribe = () => {};
    try {
      unsubscribe = subscribeProductRequests((data) => {
        setRequests(data);
        localStorage.setItem('productRequests', JSON.stringify(data));
      });
    } catch {
      const saved = JSON.parse(localStorage.getItem('productRequests') || '[]');
      setRequests(saved.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
    }
    return () => unsubscribe();
  }, []);

  const handleMenuClick = (e, request) => { setAnchorEl(e.currentTarget); setSelectedRequest(request); };
  const handleMenuClose = () => setAnchorEl(null);
  const handleDetailsOpen = () => { setDetailsOpen(true); handleMenuClose(); };
  const handleDetailsClose = () => setDetailsOpen(false);

  const handleQuoteOpen = () => {
    setQuotedPrice('');
    setQuoteAlert('');
    setQuoteOpen(true);
    handleMenuClose();
  };
  const handleQuoteClose = () => { setQuoteOpen(false); setQuoteAlert(''); };

  const handleSendQuote = async () => {
    const price = parseFloat(quotedPrice);
    if (!quotedPrice || isNaN(price) || price <= 0) {
      setQuoteAlert('Please enter a valid price greater than $0.');
      return;
    }
    setQuoteLoading(true);
    setQuoteAlert('');
    try {
      const token = Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10);
      const acceptUrl = `${window.location.origin}/accept-quote?docId=${selectedRequest._docId}&token=${token}`;

      await updateProductRequest(selectedRequest._docId, {
        status: 'Quoted',
        quotedPrice: price,
        quoteToken: token,
        quotedAt: new Date().toISOString(),
      });

      const productName = [selectedRequest.brand, selectedRequest.model].filter(Boolean).join(' ') || selectedRequest.category;
      const { html, text } = quoteEmail({
        firstName:   selectedRequest.firstName,
        refId:       selectedRequest.id || selectedRequest.refId,
        productName,
        category:    selectedRequest.category,
        brand:       selectedRequest.brand,
        model:       selectedRequest.model,
        quantity:    selectedRequest.quantity,
        quotedPrice: price,
        acceptUrl,
      });

      await notifyCustomer(selectedRequest.email, `Your Quote — ${productName} | AkdmeTech`, { html, text });
      await notifyInternal(
        `Quote Sent — ${selectedRequest.id || selectedRequest.refId}`,
        `Quote of $${price.toFixed(2)} sent to ${selectedRequest.firstName} ${selectedRequest.lastName} <${selectedRequest.email}>\nProduct: ${productName}\nAccept URL: ${acceptUrl}`,
        selectedRequest.email,
      );

      handleQuoteClose();
    } catch (err) {
      console.error(err);
      setQuoteAlert('Failed to send quote. Please try again.');
    } finally {
      setQuoteLoading(false);
    }
  };

  const handleDelete = async () => {
    const request = selectedRequest;
    handleMenuClose();
    if (request) await deleteProductRequest(request._docId);
  };

  const filteredRequests = requests.filter((r) => {
    const q = searchQuery.toLowerCase();
    return (
      `${r.firstName} ${r.lastName}`.toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.id || r.refId || '').toLowerCase().includes(q) ||
      (r.category || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ref ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Budget</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Quote</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRequests
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((request) => {
                const statusStyle = STATUS_COLORS[request.status] || STATUS_COLORS.Pending;
                return (
                  <TableRow key={request.id || request._docId} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {request.id || request.refId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {request.createdAt ? new Date(request.createdAt).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="body2">{request.firstName} {request.lastName}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{request.email}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{request.phone}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={request.category} size="small" sx={{ mb: 0.5 }} />
                      <Typography variant="body2">{request.brand} {request.model}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{request.budget || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={request.status || 'Pending'}
                        size="small"
                        sx={{ bgcolor: statusStyle.bg, color: statusStyle.color, fontWeight: 600, border: `1px solid ${statusStyle.color}` }}
                      />
                    </TableCell>
                    <TableCell>
                      {request.quotedPrice
                        ? <Typography variant="body2" sx={{ fontWeight: 700, color: '#27ae60' }}>${Number(request.quotedPrice).toFixed(2)}</Typography>
                        : <Typography variant="caption" color="text.secondary">—</Typography>
                      }
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={(e) => handleMenuClick(e, request)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredRequests.length}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />

      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDetailsOpen}>View Details</MenuItem>
        <MenuItem onClick={handleQuoteOpen} sx={{ color: '#2980b9' }}>
          <LocalOfferIcon sx={{ fontSize: 18, mr: 1 }} /> Send Quote
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>Delete Request</MenuItem>
      </Menu>

      {/* Details Dialog */}
      {selectedRequest && (
        <Dialog open={detailsOpen} onClose={handleDetailsClose} maxWidth="sm" fullWidth
          PaperProps={{ sx: { background: 'rgba(45,52,54,0.95)', backdropFilter: 'blur(10px)', borderRadius: '15px', border: '1px solid rgba(255,255,255,0.1)' } }}>
          <DialogTitle>Product Request — {selectedRequest.id || selectedRequest.refId}</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Customer</Typography>
                <Typography variant="body1">{selectedRequest.firstName} {selectedRequest.lastName}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedRequest.email}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedRequest.phone}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">Product Details</Typography>
                <Typography variant="body2"><strong>Category:</strong> {selectedRequest.category}</Typography>
                <Typography variant="body2"><strong>Brand:</strong> {selectedRequest.brand || '—'}</Typography>
                <Typography variant="body2"><strong>Model:</strong> {selectedRequest.model || '—'}</Typography>
                <Typography variant="body2"><strong>Quantity:</strong> {selectedRequest.quantity || 1}</Typography>
                <Typography variant="body2"><strong>Budget:</strong> {selectedRequest.budget || '—'}</Typography>
              </Box>
              {selectedRequest.specs && Object.keys(selectedRequest.specs).length > 0 && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Specifications</Typography>
                  {Object.entries(selectedRequest.specs).map(([k, v]) => v
                    ? <Typography key={k} variant="body2"><strong>{k}:</strong> {v}</Typography>
                    : null
                  )}
                </Box>
              )}
              {selectedRequest.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Additional Notes</Typography>
                  <Typography variant="body1">{selectedRequest.notes}</Typography>
                </Box>
              )}
              {selectedRequest.quotedPrice && (
                <Box sx={{ p: 2, bgcolor: 'rgba(39,174,96,0.1)', borderRadius: 2, border: '1px solid rgba(39,174,96,0.3)' }}>
                  <Typography variant="subtitle2" color="text.secondary">Quote Sent</Typography>
                  <Typography variant="h6" sx={{ color: '#27ae60', fontWeight: 700 }}>${Number(selectedRequest.quotedPrice).toFixed(2)}</Typography>
                  <Typography variant="caption" color="text.secondary">Sent on {selectedRequest.quotedAt ? new Date(selectedRequest.quotedAt).toLocaleDateString() : '—'}</Typography>
                </Box>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDetailsClose}>Close</Button>
            <Button onClick={() => { handleDetailsClose(); handleQuoteOpen(); }} variant="contained"
              sx={{ background: 'linear-gradient(135deg, #2980b9, #1abc9c)', color: '#fff' }}
              startIcon={<LocalOfferIcon />}>
              {selectedRequest.quotedPrice ? 'Resend Quote' : 'Send Quote'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Send Quote Dialog */}
      {selectedRequest && (
        <Dialog open={quoteOpen} onClose={handleQuoteClose} maxWidth="xs" fullWidth
          PaperProps={{ sx: { background: 'rgba(45,52,54,0.97)', backdropFilter: 'blur(10px)', borderRadius: '15px', border: '1px solid rgba(255,255,255,0.1)' } }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalOfferIcon sx={{ color: '#2980b9' }} /> Send Quote
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Enter the price for <strong>{[selectedRequest.brand, selectedRequest.model].filter(Boolean).join(' ') || selectedRequest.category}</strong> requested by <strong>{selectedRequest.firstName} {selectedRequest.lastName}</strong>.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              An email with an <strong>"Accept & Pay"</strong> button will be sent to <strong>{selectedRequest.email}</strong>.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              label="Quoted Price (USD)"
              type="number"
              value={quotedPrice}
              onChange={(e) => setQuotedPrice(e.target.value)}
              inputProps={{ min: 1, step: '0.01' }}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1, color: 'text.secondary' }}>$</Typography> }}
              sx={{ mt: 2 }}
              onKeyPress={(e) => { if (e.key === 'Enter') handleSendQuote(); }}
            />
            {quoteAlert && <Alert severity="error" sx={{ mt: 2 }}>{quoteAlert}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleQuoteClose} disabled={quoteLoading}>Cancel</Button>
            <Button onClick={handleSendQuote} variant="contained" disabled={quoteLoading}
              sx={{ background: 'linear-gradient(135deg, #2980b9, #1abc9c)', color: '#fff', minWidth: 140 }}>
              {quoteLoading ? <CircularProgress size={20} color="inherit" /> : 'Send Quote Email'}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}

export default ProductRequests;
